import { useSyncExternalStore } from 'react';

/**
 * Системное окно установки на экран «Домой» (3.16.10).
 *
 * Есть только у браузеров на Chromium вне iOS: они присылают странице событие
 * `beforeinstallprompt`, и его можно придержать до нажатия кнопки. На iOS такого
 * нет ни в одном браузере — все они на WebKit. Окно «Поделиться», открытое самой
 * страницей (`navigator.share`), пункта «На экран Домой» тоже не содержит:
 * проверено на iPhone владельцем, 2026-09-13. Там остаются шаги словами.
 *
 * Событие приходит один раз и рано — нередко раньше, чем React что-то
 * нарисовал, — поэтому слушатель ставится в `main.tsx` до отрисовки.
 */

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | undefined;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

export function listenForInstallPrompt(): void {
  globalThis.addEventListener?.('beforeinstallprompt', (event) => {
    // Без `preventDefault` часть браузеров покажет свою плашку сама и в
    // случайный момент, а не тогда, когда человек дочитал, зачем это нужно.
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    announce();
  });

  globalThis.addEventListener?.('appinstalled', () => {
    deferred = undefined;
    announce();
  });
}

/** Показать системное окно. Возвращает, согласился ли человек установить. */
export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (event === undefined) return false;

  // Окно показывается один раз на событие: повторный `prompt()` бросает, а
  // кнопка, которая ничего не делает, хуже отсутствующей.
  deferred = undefined;
  announce();

  await event.prompt();
  return (await event.userChoice).outcome === 'accepted';
}

export function useInstallAvailable(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    () => deferred !== undefined,
    () => false,
  );
}
